/**
 * deployment-menu.js
 */

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useLocation, useParams, matchPath } from 'react-router-dom';
import { Types } from 'utils/constants';
import { makeLocation, makeDeploymentSectionPath } from 'utils/routing';
import useRecordStore from 'contexts/record-store';
import DrawerMenu from 'components/menu-drawer/menu';
import DrawerMenuItem from 'components/menu-drawer/menu/drawer-menu-item';
import Icon from 'components/icon';
import DeploymentMenuItem from './deployment-menu-item';
import DeploymentSubMenuItem from './deployment-sub-menu-item';
import { ReactComponent as DeploymentSVG } from 'assets/images/deployment.svg';


const useStyles = makeStyles(theme => ({
  indentContainer: {
    paddingLeft: theme.spacing(6)
  },
}));


const IncompleteLabel = (props) => {
  const { label } = props;
  return (
    <div 
      className='flex-hc' 
      style={{ justifyContent: 'space-between' }}
    >
      <div>{label}</div>
      <Icon type='incomplete' />
    </div>
  );
}


export default function DeploymentMenu(props) {
  const {
    basePath,
  } = props;
  let location = useLocation();
  let params = useParams();
  let {
    deploymentId,
    editSectionId,
  } = params;
  deploymentId = parseInt(deploymentId);
  const recordStore = useRecordStore();

  const deploymentRecord = recordStore.useRecord(Types.Record.DEPLOYMENT, deploymentId).ornull;
  const hasRecord = Boolean(deploymentRecord);
  const patrol = hasRecord ? deploymentRecord.get().patrol : null;
  const detection = hasRecord ? deploymentRecord.get().detection : null;

  const isEditor = Boolean(editSectionId);
  const classes = useStyles();
  const [menuGroups, setMenuGroups] = React.useState();
  const workMode = deploymentRecord?.get()?.workMode;
  const patrolTypesString = deploymentRecord?.get()?.patrol?.patrolTypes?.map(pt => pt.id)?.join('-') || '';
  const isNewPatrolTypeRequest = Boolean(
    editSectionId === makeDeploymentSectionPath(Types.DeploymentSection.PATROL)
  );
  
  // Check if deployment is "Not performed" based on fulfillmentState
  const isNotPerformed = hasRecord && (
    deploymentRecord.get().fulfillmentState === Types.FulfillmentState.DogNotDeployed || 
    deploymentRecord.get().fulfillmentState === Types.FulfillmentState.CancelledEnroute
  );



  // generates a menu item
  const generateMenuItem = React.useCallback((sectionType, record, isSubItem, locationParams, step, patrolIndex, isSectionComplete) => {
    const sectionPath = makeDeploymentSectionPath(sectionType, record);
    const activeLocation = makeLocation(
      basePath,
      'deployment',
      locationParams.pathItemName,
      locationParams.pathParams,
      location
    );

    const isActive = Boolean(matchPath(location.pathname, activeLocation.pathname)?.isExact);
    const isEditing = sectionPath === editSectionId;
    const iconName = isEditing
      ? 'edit'
      : isSectionComplete
        ? 'complete'
        : 'incomplete';

    const itemProps = {
      type: sectionType,
      record,
      key: `${deploymentId}-${sectionType}-${record?.id}`,
      to: activeLocation,
      isActive: isSubItem && isActive,
      basePath,
      patrolIndex,
      step,
      iconName
    };

    if (!isSubItem) {
      return <DeploymentMenuItem {...itemProps} />;
    } else {
      return <DeploymentSubMenuItem {...itemProps} />;
    }
  }, [
    deploymentId,
    editSectionId,
    location,
    basePath,
  ]);


  // build menu
  React.useEffect(() => {
    if (hasRecord) {
      setMenuGroups([

        {
          items: <>
            {/* top deployment item */}
            {generateMenuItem(
              null,
              deploymentRecord.get(),
              false,
              {
                pathItemName: isEditor
                  ? 'edit'
                  : 'view',
                pathParams: {
                  deploymentId,
                  viewSectionId: makeDeploymentSectionPath(Types.DeploymentSection.GENERAL),
                  editSectionId,
                }
              },
            )}
          </>
        },


        {
          items: <>

            {/* indent container */}
            <div className={classes.indentContainer}>

              {/* general */}
              {generateMenuItem(
                Types.DeploymentSection.GENERAL,
                deploymentRecord.get(),
                true,
                {
                  pathItemName: isEditor
                    ? 'edit'
                    : 'view',
                  pathParams: {
                    deploymentId,
                    viewSectionId: makeDeploymentSectionPath(Types.DeploymentSection.GENERAL),
                    editSectionId,
                  }
                },
                null,
                null,
                true,
              )}


              {/* patrol types */}
              {deploymentId > 0 && Boolean(patrol) && patrol.patrolTypes && !isNotPerformed &&
                <>
                  {patrol.patrolTypes.map((pt, patrolIndex) => {
                    return generateMenuItem(
                      Types.DeploymentSection.PATROL,
                      pt,
                      true,
                      {
                        pathItemName: isEditor
                          ? 'edit'
                          : 'view',
                        pathParams: {
                          deploymentId,
                          viewSectionId: makeDeploymentSectionPath(Types.DeploymentSection.PATROL, pt),
                          editSectionId,
                        }
                      },
                      '2',
                      patrolIndex,
                      true
                    );
                  })}
                </>}


                {/* new patrol type */}
                {isNewPatrolTypeRequest && !isNotPerformed && generateMenuItem(
                  Types.DeploymentSection.PATROL,
                  // `patrol-new`,
                  null,
                  true,
                  {
                    pathItemName: 'edit',
                    pathParams: {
                      deploymentId,
                      viewSectionId: makeDeploymentSectionPath(Types.DeploymentSection.PATROL),
                      editSectionId,
                    }
                  },
                  '',
                  patrol.patrolTypes.length,
                  false
                )}


                {/* detection item */}
                {deploymentId > 0 && Boolean(detection) && !isNotPerformed &&
                <>
                  {generateMenuItem(
                    Types.DeploymentSection.DETECTION,
                    detection,
                    true,
                    {
                      pathItemName: isEditor
                        ? 'edit'
                        : 'view',
                      pathParams: {
                        deploymentId: deploymentId,
                        viewSectionId: makeDeploymentSectionPath(Types.DeploymentSection.DETECTION),
                        editSectionId,
                      }
                    },
                    '2',
                    null,
                    true
                  )}
                </>}


                {/* disabled generic step 2 item */}
                {deploymentId === 0 && !isNotPerformed &&
                <DrawerMenuItem
                  id={`deployment-step-2`}
                  icon={<Icon type='patrol-other' />}
                  label={<IncompleteLabel label='Detection / Patrol' />}
                  disabled
                />}


                {/* summary item */}
                {deploymentId > 0 &&
                generateMenuItem(
                  Types.DeploymentSection.SUMMARY,
                  deploymentRecord.get(),
                  true,
                  {
                    pathItemName: isEditor
                      ? 'edit'
                      : 'view',
                    pathParams: {
                      deploymentId,
                      viewSectionId: makeDeploymentSectionPath(Types.DeploymentSection.SUMMARY),
                      editSectionId,
                    }
                  },
                  '3',
                  null,
                  deploymentRecord.get().summary.comments !== '',
                )}


                {/* disabled summary item */}
                {deploymentId === 0 &&
                <DrawerMenuItem
                  id={`deployment-step-3`}
                  icon={<Icon type='comment' />}
                  label={<IncompleteLabel label='Summary' />}
                  disabled
                />}


            </div>
          </>
        },
      ]);
    }

  // deploymentRecord is hook state var
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasRecord,
    location,
    deploymentId,
    workMode,
    patrolTypesString,
    editSectionId,
    generateMenuItem,
    isEditor
  ]);


  return (
    <>
      {menuGroups &&
      <DrawerMenu
        menuGroups={menuGroups}
        footer={<DeploymentSVG />}
      />}
    </>
  );
}